import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Check, MessageSquare, Facebook, Instagram, MessageCircle } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import PublicFooter from '../components/PublicFooter';

const Pricing = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
                <MessageSquare className="h-8 w-8 text-blue-600" />
                <span className="text-2xl font-bold text-gray-900">ChatFlow</span>
              </div>
              <LanguageSwitcher />
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/login')}>{t('login')}</Button>
              <Button onClick={() => navigate('/signup')}>{t('getStarted')}</Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            {t('choosePlan')}
          </h1>
          <p className="text-xl text-gray-600 mb-6">
            {t('pricingSubtitle')}
          </p>
          <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full">
            <Check className="h-5 w-5 mr-2" />
            {t('allPlansInclude')}
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {/* Free Plan */}
          <Card className="relative transition-all hover:shadow-2xl">
            <CardHeader className="text-center pb-8 pt-8">
              <CardTitle className="text-2xl mb-2">{t('free')}</CardTitle>
              <CardDescription className="text-sm mb-6">{t('freeDesc')}</CardDescription>
              <div className="mb-4">
                <span className="text-5xl font-bold text-gray-900">$0</span>
                <span className="text-gray-600">/month</span>
              </div>
              <Button
                className="w-full"
                onClick={() => navigate('/checkout', { state: { plan: 'Free', price: 0 } })}
              >
                {t('getStarted')} {t('free')}
              </Button>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">50 {t('contacts')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{t('connectChannels')} 2 {t('channels')}</span>
                </li>
                <li className="flex items-start ml-8">
                  <div className="flex space-x-2">
                    <Facebook className="h-5 w-5 text-blue-600" />
                    <Instagram className="h-5 w-5 text-pink-600" />
                    <MessageCircle className="h-5 w-5 text-green-600" />
                  </div>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">1 {t('user')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{t('emailSupport')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-xs text-gray-500 italic">{t('chatFlowBranding')}</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Starter Plan */}
          <Card className="relative transition-all hover:shadow-2xl">
            <CardHeader className="text-center pb-8 pt-8">
              <CardTitle className="text-2xl mb-2">{t('starter')}</CardTitle>
              <CardDescription className="text-sm mb-4">{t('starterDesc')}</CardDescription>
              <div className="mb-2">
                <span className="text-4xl font-bold text-gray-900">$9</span>
                <span className="text-gray-600">/month</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">{t('billedYearly')} (20% {t('off')})</p>
              <p className="text-xs text-gray-600">{t('or')} $12 {t('monthly')}</p>
              <Button
                className="w-full mt-4"
                onClick={() => navigate('/checkout', { state: { plan: 'Starter', price: 9 } })}
              >
                {t('startFreeTrial')}
              </Button>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">1,000 {t('contacts')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{t('connectChannels')} 2 {t('channels')}</span>
                </li>
                <li className="flex items-start ml-8">
                  <div className="flex space-x-2">
                    <Facebook className="h-5 w-5 text-blue-600" />
                    <Instagram className="h-5 w-5 text-pink-600" />
                    <MessageCircle className="h-5 w-5 text-green-600" />
                  </div>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">2 {t('users')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{t('emailSupport')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{t('basicAnalytics')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{t('noBranding')}</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Professional Plan */}
          <Card className="relative transition-all hover:shadow-2xl border-2 border-blue-600 shadow-xl scale-105">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <Badge className="bg-blue-600 text-white px-4 py-1 text-sm">
                {t('mostPopular')}
              </Badge>
            </div>
            <CardHeader className="text-center pb-8 pt-8">
              <CardTitle className="text-2xl mb-2">{t('professional')}</CardTitle>
              <CardDescription className="text-sm mb-4">{t('professionalDesc')}</CardDescription>
              <div className="mb-2">
                <span className="text-4xl font-bold text-gray-900">$18</span>
                <span className="text-gray-600">/month</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">{t('billedYearly')} (25% {t('off')})</p>
              <p className="text-xs text-gray-600">{t('or')} $22 {t('monthly')}</p>
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 mt-4"
                onClick={() => navigate('/checkout', { state: { plan: 'Professional', price: 18 } })}
              >
                {t('startFreeTrial')}
              </Button>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">4,000 {t('contacts')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{t('connectChannels')} 3 {t('channels')}</span>
                </li>
                <li className="flex items-start ml-8">
                  <div className="flex space-x-2">
                    <Facebook className="h-5 w-5 text-blue-600" />
                    <Instagram className="h-5 w-5 text-pink-600" />
                    <MessageCircle className="h-5 w-5 text-green-600" />
                  </div>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">3 {t('users')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{t('emailSupport')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{t('basicAnalytics')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{t('noBranding')}</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Enterprise Plan */}
          <Card className="relative transition-all hover:shadow-2xl">
            <CardHeader className="text-center pb-8 pt-8">
              <CardTitle className="text-2xl mb-2">{t('enterprise')}</CardTitle>
              <CardDescription className="text-sm mb-4">{t('enterpriseDesc')}</CardDescription>
              <div className="mb-2">
                <span className="text-4xl font-bold text-gray-900">$49</span>
                <span className="text-gray-600">/month</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">{t('billedYearly')} (30% {t('off')})</p>
              <p className="text-xs text-gray-600">{t('or')} $64 {t('monthly')}</p>
              <Button
                className="w-full mt-4"
                onClick={() => navigate('/checkout', { state: { plan: 'Enterprise', price: 49 } })}
              >
                {t('startFreeTrial')}
              </Button>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">10,000 {t('contacts')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{t('connectChannels')} 3 {t('channels')}</span>
                </li>
                <li className="flex items-start ml-8">
                  <div className="flex space-x-2">
                    <Facebook className="h-5 w-5 text-blue-600" />
                    <Instagram className="h-5 w-5 text-pink-600" />
                    <MessageCircle className="h-5 w-5 text-green-600" />
                  </div>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">3 {t('users')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{t('emailSupport')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{t('basicAnalytics')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{t('noBranding')}</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">{t('faqTitle')}</h2>
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('faqQ1')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  {t('faqA1')}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('faqQ2')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  {t('faqA2')}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('faqQ3')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  {t('faqA3')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      <PublicFooter />
    </div>
  );
};

export default Pricing;
